import "../users/AddUserForm.css";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar/Navbar";
import TopHeader from "../components/Navbar/TopHeader";
import { apiCall } from "../api/apiCall";
import generateToastContainer from "../utils/ToastContainer";
import { User } from "../models/user";
import { Company } from "../models/company";
import { useTranslation } from "../i18n/UseTranslation";
import { getAuth, onAuthStateChanged } from "firebase/auth";

type RegisterAdminResponse = {
  token: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
};

function AddAdminForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const translations = t.addUserForm;
  const roleTranslations = translations.roles.admin;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCompanyUuid, setSelectedCompanyUuid] =
    useState("");

  const [currentUser, setCurrentUser] =
    useState<User | null>(null);

  const [companies, setCompanies] =
    useState<Company[]>([]);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, () => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const initForm = async () => {
      if (!isAuthReady) return;

      try {
        const userProfile = await apiCall<User>("/users/me", { method: "GET" });
        setCurrentUser(userProfile);

        if (userProfile.role?.name === "super_admin") {
          const allCompanies = await apiCall<Company[]>("/companies", { method: "GET" });
          setCompanies(allCompanies);

          if (allCompanies.length > 0) {
            setSelectedCompanyUuid(allCompanies[0].uuid);
          }
        } else {
          setSelectedCompanyUuid(userProfile.company?.uuid ?? "");
        }
      } catch (error) {
        console.error("Initialization error:", error);
        toast.error(translations.initializationError);
      } finally {
        setIsLoading(false);
      }
    };

    initForm();
  }, [translations.initializationError, isAuthReady]);

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !password
    ) {
      toast.error(translations.requiredFields);
      return;
    }

    const isSuperAdmin =
      currentUser?.role?.name === "super_admin";

    if (
      isSuperAdmin &&
      !selectedCompanyUuid
    ) {
      toast.error(translations.selectCompany);
      return;
    }

    setIsSubmitting(true);

    try {
      const response =
        await apiCall<RegisterAdminResponse>(
          "/register/admin",
          {
            method: "POST",
            body: JSON.stringify({
              token:
                localStorage.getItem(
                  "auth_token"
                ),
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              email: email.trim(),
              password,
              phone_number:
                phoneNumber.trim() || null,
              company_uuid:
                selectedCompanyUuid || null,
            }),
          }
        );

      const fullName =
        `${response.first_name} ${response.last_name}`.trim();

      toast.success(
        roleTranslations.success.replace(
          "{name}",
          fullName
        )
      );

      navigate("/admins");
    } catch (error) {
      console.error(
        "Error creating administrator:",
        error
      );

      const message =
        error instanceof Error
          ? error.message
          : translations.unknownError;

      toast.error(
        roleTranslations.error.replace(
          "{message}",
          message
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="add-user-page">
        <Navbar />
        <TopHeader />

        <main className="dashboard-wrapper">
          <div className="add-user-loading">
            <div
              className="add-user-spinner"
              aria-hidden="true"
            />

            <p>{translations.loading}</p>
          </div>
        </main>

        {generateToastContainer()}
      </div>
    );
  }

  const isSuperAdmin =
    currentUser?.role?.name === "super_admin";

  return (
    <div className="add-user-page">
      <Navbar />
      <TopHeader />

      <main className="dashboard-wrapper">
        <div className="add-user-wrapper">
          <form
            className="add-user-card"
            onSubmit={handleSubmit}
          >
            <h1 className="add-user-title">
              {roleTranslations.title}
            </h1>

            <div className="add-user-grid">
              {isSuperAdmin && (
                <label className="add-user-field add-user-field-full">
                  <span className="add-user-label">
                    {translations.targetCompany}
                  </span>

                  <select
                    className="add-user-input"
                    value={selectedCompanyUuid}
                    onChange={(event) =>
                      setSelectedCompanyUuid(
                        event.target.value
                      )
                    }
                    required
                  >
                    {companies.length === 0 && (
                      <option value="">
                        {translations.noCompanies}
                      </option>
                    )}

                    {companies.map((company) => (
                      <option
                        key={company.uuid}
                        value={company.uuid}
                      >
                        {company.name}
                        {company.is_platform
                          ? ` (${translations.platform})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="add-user-field">
                <span className="add-user-label">
                  {translations.firstName}
                </span>

                <input
                  className="add-user-input"
                  type="text"
                  value={firstName}
                  onChange={(event) =>
                    setFirstName(
                      event.target.value
                    )
                  }
                  placeholder={
                    translations.placeholders.firstName
                  }
                  required
                />
              </label>

              <label className="add-user-field">
                <span className="add-user-label">
                  {translations.lastName}
                </span>

                <input
                  className="add-user-input"
                  type="text"
                  value={lastName}
                  onChange={(event) =>
                    setLastName(
                      event.target.value
                    )
                  }
                  placeholder={
                    translations.placeholders.lastName
                  }
                  required
                />
              </label>

              <label className="add-user-field">
                <span className="add-user-label">
                  {translations.email}
                </span>

                <input
                  className="add-user-input"
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(
                      event.target.value
                    )
                  }
                  placeholder={
                    translations.placeholders.email
                  }
                  required
                />
              </label>

              <label className="add-user-field">
                <span className="add-user-label">
                  {translations.password}
                </span>

                <input
                  className="add-user-input"
                  type="password"
                  value={password}
                  onChange={(event) =>
                    setPassword(
                      event.target.value
                    )
                  }
                  placeholder={
                    translations.placeholders.password
                  }
                  required
                />
              </label>

              <label className="add-user-field add-user-field-full">
                <span className="add-user-label">
                  {translations.phoneNumber}{" "}
                  <small>({translations.optional})</small>
                </span>

                <input
                  className="add-user-input"
                  type="tel"
                  value={phoneNumber}
                  onChange={(event) =>
                    setPhoneNumber(
                      event.target.value
                    )
                  }
                  placeholder={
                    translations.placeholders.phoneNumber
                  }
                />
              </label>
            </div>

            <div className="add-user-actions">
              <button
                type="button"
                className="add-user-cancel"
                onClick={() =>
                  navigate("/admins")
                }
                disabled={isSubmitting}
              >
                {translations.cancel}
              </button>

              <button
                type="submit"
                className="add-user-submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? translations.creating
                  : roleTranslations.create}
              </button>
            </div>
          </form>
        </div>
      </main>

      {generateToastContainer()}
    </div>
  );
}

export default AddAdminForm;